import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { QrCode, ScanLine, X, Check, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isStandaloneApp } from "./NotificationPrefs";

const APP_HDR = { headers: { "X-Rosterly-App": "1" } };

// New device (e.g. work laptop): one-time QR, refreshed every 2 minutes, polled until the phone approves.
export function QrLoginPanel({ onClose }) {
    const [s, setS] = useState(null);
    const [left, setLeft] = useState(0);
    const [noSave, setNoSave] = useState(false);
    const noSaveRef = useRef(false);
    noSaveRef.current = noSave;
    const [gen, setGen] = useState(0);

    useEffect(() => {
        let stop = false, timer = null;
        (async () => {
            const { data } = await api.post("/auth/qr/start");
            if (stop) return;
            setS(data); setLeft(data.expires_in);
            const poll = async () => {
                if (stop) return;
                try {
                    const { data: r } = await api.post("/auth/qr/poll", { session_id: data.session_id, secret: data.secret });
                    if (stop) return;
                    if (r.status === "complete") {
                        localStorage.setItem("rosterly_token", r.token);
                        if (noSaveRef.current) { localStorage.setItem("rosterly_nosave", "1"); sessionStorage.setItem("rosterly_alive", "1"); }
                        else localStorage.removeItem("rosterly_nosave");
                        window.location.href = "/";
                        return;
                    }
                } catch { if (!stop) setGen((g) => g + 1); return; }
                timer = setTimeout(poll, 2000);
            };
            poll();
        })().catch(() => toast.error("Couldn't create a sign-in code"));
        return () => { stop = true; clearTimeout(timer); };
    }, [gen]);
    useEffect(() => {
        if (!s) return undefined;
        const t = setInterval(() => setLeft((x) => { if (x <= 1) { setGen((g) => g + 1); return 0; } return x - 1; }), 1000);
        return () => clearInterval(t);
    }, [s]);

    return (
        <div className="space-y-3 text-center" data-testid="qr-login-panel">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#9D4CDD]">Sign in with your phone</span>
                <button type="button" onClick={onClose} aria-label="Close" data-testid="qr-login-close" className="h-6 w-6 inline-flex items-center justify-center rounded-full text-white/50 hover:text-white"><X className="h-3.5 w-3.5" /></button>
            </div>
            <div className="mx-auto w-[212px] h-[212px] rounded-xl bg-white p-3 flex items-center justify-center" data-keep-colors data-testid="qr-login-code">
                {s ? <QRCodeSVG value={s.qr} size={188} marginSize={0} title="One-time sign-in code" /> : <RotateCw className="h-6 w-6 text-black/40 animate-spin" />}
            </div>
            <p className="text-[12px] text-white/60 leading-snug">Open the Rosterly app on your phone → <b className="text-white">My Account</b> → <b className="text-white">Sign in a device</b>, then scan this code.</p>
            <p className="text-[11px] text-white/40" data-testid="qr-login-timer">New code in {left}s</p>
            <label className="flex items-center justify-center gap-2 text-[12px] text-white/80 cursor-pointer select-none">
                <input type="checkbox" checked={noSave} onChange={(e) => setNoSave(e.target.checked)} data-testid="qr-login-nosave" className="h-4 w-4 accent-[#9D4CDD]" />
                Don't save this account on this device
            </label>
        </div>
    );
}

function ApproveSheet({ onClose }) {
    const [qr, setQr] = useState(null);
    const [device, setDevice] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const onScan = async (codes) => {
        const v = codes?.[0]?.rawValue;
        if (!v || qr || busy) return;
        if (!v.startsWith("rosterly-qr:v1:")) { toast.error("That isn't a Rosterly sign-in code"); return; }
        setBusy(true);
        try { const { data } = await api.post("/auth/qr/peek", { qr: v }, APP_HDR); setQr(v); setDevice(data.device); }
        catch (e) { toast.error(e?.response?.data?.detail || "Code expired — refresh it on the other device"); }
        setBusy(false);
    };
    const approve = async () => {
        setBusy(true);
        try { await api.post("/auth/qr/approve", { qr }, APP_HDR); setDone(true); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve"); setQr(null); }
        setBusy(false);
    };
    return (
        <div className="fixed inset-0 z-[100060] bg-black/90 backdrop-blur flex flex-col items-center justify-center p-5 gap-4" data-testid="qr-approve-sheet">
            <button type="button" onClick={onClose} aria-label="Close" data-testid="qr-approve-close" className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 text-white"><X className="h-5 w-5" /></button>
            {done ? (
                <div className="text-center space-y-3" data-testid="qr-approve-done">
                    <Check className="h-10 w-10 text-[#22C55E] mx-auto" />
                    <p className="text-white font-bold">{device} is now signed in</p>
                    <button type="button" onClick={onClose} className="btn-primary px-6" data-testid="qr-approve-finish">Done</button>
                </div>
            ) : qr ? (
                <div className="text-center space-y-4 max-w-xs" data-testid="qr-approve-confirm">
                    <p className="text-white text-lg font-bold">Sign in on {device}?</p>
                    <p className="text-[12px] text-white/55">Only approve if you are in front of that device right now.</p>
                    <div className="flex gap-2 justify-center">
                        <button type="button" onClick={() => setQr(null)} className="px-4 py-2 rounded-md border border-white/20 text-white text-sm font-bold" data-testid="qr-approve-cancel">Cancel</button>
                        <button type="button" onClick={approve} disabled={busy} className="btn-primary px-5 disabled:opacity-60" data-testid="qr-approve-confirm-btn">{busy ? "One sec…" : "Approve"}</button>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-white font-bold">Scan the code on the other device</p>
                    <div className="w-full max-w-xs aspect-square rounded-2xl overflow-hidden" data-testid="qr-approve-camera">
                        <Scanner onScan={onScan} paused={busy} formats={["qr_code"]} onError={() => toast.error("Camera unavailable — allow camera access for Rosterly")} />
                    </div>
                </>
            )}
        </div>
    );
}

// My Account card on the phone: scanning only works inside the installed app.
export function QrDeviceCard({ Card }) {
    const [open, setOpen] = useState(false);
    const inApp = isStandaloneApp();
    const impersonating = !!localStorage.getItem("rosterly_owner_token");
    return (
        <Card icon={QrCode} title="Sign In A Device" testid="account-qr-card">
            <p className="text-[12px] text-white/60 mb-3">On another device (like a work laptop), pick "Sign in with QR code", then scan it here to sign it in. Scanning only works in the Rosterly app on your phone.</p>
            {inApp && !impersonating ? (
                <button type="button" onClick={() => setOpen(true)} data-testid="qr-scan-btn"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white text-[#0A0B10] text-xs font-black uppercase tracking-wider">
                    <ScanLine className="h-4 w-4" />Scan code
                </button>
            ) : <p className="text-[12px] text-white/45" data-testid="qr-scan-unavailable">{impersonating ? "Switch back to your own account to sign in a device." : "Open the Rosterly app on your phone to scan."}</p>}
            {open && <ApproveSheet onClose={() => setOpen(false)} />}
        </Card>
    );
}
